import React, { useState, useEffect, useMemo } from 'react';
import { 
    getAdminContactMessages, 
    updateContactMessageStatus, 
    deleteContactMessage 
} from '../../services/api';
import { 
    Mail, Phone, Clock, Search, RefreshCw, Trash2, 
    CheckCircle2, AlertCircle, HelpCircle, MessageSquare
} from 'lucide-react';

const ContactMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [updatingId, setUpdatingId] = useState(null);

    const fetchMessages = async () => {
        setLoading(true);
        try {
            const res = await getAdminContactMessages();
            if (res.success) {
                setMessages(res.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch contact messages:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
    }, []);

    const handleStatusChange = async (msgId, newStatus) => {
        setUpdatingId(msgId);
        try {
            const res = await updateContactMessageStatus(msgId, newStatus);
            if (res.success) {
                setMessages(prev => prev.map(m => m.id === msgId ? { ...m, status: newStatus } : m));
            }
        } catch (error) {
            console.error("Failed to update status:", error);
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDelete = async (msgId) => {
        if (!window.confirm("Are you sure you want to delete this message?")) return;
        try {
            const res = await deleteContactMessage(msgId);
            if (res.success) {
                setMessages(prev => prev.filter(m => m.id !== msgId));
            }
        } catch (error) {
            console.error("Failed to delete message:", error);
        }
    };

    const formatDateTime = (isoStr) => {
        if (!isoStr) return 'N/A';
        try {
            const d = new Date(isoStr);
            return d.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return isoStr;
        }
    };

    const stats = useMemo(() => {
        const total = messages.length;
        const pending = messages.filter(m => m.status === 'Pending').length;
        const replied = messages.filter(m => m.status === 'Replied').length;
        const resolved = messages.filter(m => m.status === 'Resolved').length;
        return { total, pending, replied, resolved };
    }, [messages]);

    const filteredMessages = useMemo(() => {
        return messages.filter(m => {
            if (statusFilter !== 'all' && m.status !== statusFilter) return false;

            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const matchesName = (m.name || '').toLowerCase().includes(q);
                const matchesEmail = (m.email || '').toLowerCase().includes(q);
                const matchesPhone = (m.phone || '').toLowerCase().includes(q);
                const matchesSubject = (m.subject || '').toLowerCase().includes(q);
                const matchesMessage = (m.message || '').toLowerCase().includes(q);
                return matchesName || matchesEmail || matchesPhone || matchesSubject || matchesMessage;
            }

            return true;
        });
    }, [messages, searchQuery, statusFilter]);

    return (
        <div className="p-2 sm:p-4 max-w-7xl mx-auto space-y-3 font-sans text-xs">
            
            {/* Header Bar */}
            <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                    <div className="flex items-center gap-1.5 text-emerald-600 font-bold text-[11px] uppercase tracking-wide">
                        <MessageSquare size={14} /> Support & Contact Inquiries
                    </div>
                    <h1 className="text-base sm:text-lg font-black text-gray-900 dark:text-white mt-0.5">
                        Contact Us Messages
                    </h1>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        View customer inquiries, phone numbers, and manage message statuses.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchMessages}
                        className="px-3 py-1.5 bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1.5 text-xs font-bold"
                    >
                        <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            {/* KPI Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Messages</p>
                        <h3 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white mt-0.5">
                            {stats.total}
                        </h3>
                        <p className="text-[10px] text-gray-400">All submissions</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950 text-blue-600 flex items-center justify-center">
                        <Mail size={16} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pending</p>
                        <h3 className="text-lg sm:text-xl font-black text-amber-500 mt-0.5">
                            {stats.pending}
                        </h3>
                        <p className="text-[10px] text-amber-600">Awaiting reply</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-950 text-amber-500 flex items-center justify-center">
                        <AlertCircle size={16} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Replied</p>
                        <h3 className="text-lg sm:text-xl font-black text-purple-600 mt-0.5">
                            {stats.replied}
                        </h3>
                        <p className="text-[10px] text-purple-600">Response sent</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-950 text-purple-600 flex items-center justify-center">
                        <HelpCircle size={16} />
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Resolved</p>
                        <h3 className="text-lg sm:text-xl font-black text-emerald-600 mt-0.5">
                            {stats.resolved}
                        </h3>
                        <p className="text-[10px] text-emerald-600">Closed issues</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-600 flex items-center justify-center">
                        <CheckCircle2 size={16} />
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-white dark:bg-gray-800 p-2.5 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                {/* Search Bar */}
                <div className="relative flex-1 sm:w-64">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        placeholder="Search by name, email, phone number..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-7 pr-2.5 py-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-[11px] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                </div>

                {/* Status Tabs */}
                <div className="flex items-center bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-0.5 text-[11px]">
                    <button
                        onClick={() => setStatusFilter('all')}
                        className={`px-2 py-0.5 font-bold rounded ${statusFilter === 'all' ? 'bg-white dark:bg-gray-800 text-emerald-600 shadow-xs' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                        All ({stats.total})
                    </button>
                    <button
                        onClick={() => setStatusFilter('Pending')}
                        className={`px-2 py-0.5 font-bold rounded ${statusFilter === 'Pending' ? 'bg-amber-500 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                        Pending ({stats.pending})
                    </button>
                    <button
                        onClick={() => setStatusFilter('Replied')}
                        className={`px-2 py-0.5 font-bold rounded ${statusFilter === 'Replied' ? 'bg-purple-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                        Replied ({stats.replied})
                    </button>
                    <button
                        onClick={() => setStatusFilter('Resolved')}
                        className={`px-2 py-0.5 font-bold rounded ${statusFilter === 'Resolved' ? 'bg-emerald-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-800'}`}
                    >
                        Resolved ({stats.resolved})
                    </button>
                </div>
            </div>

            {/* Messages Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="p-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-xs font-extrabold text-gray-900 dark:text-white">Contact Messages List</h2>
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 rounded border border-emerald-200">
                        {filteredMessages.length} entries
                    </span>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-2">
                        <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
                        <p className="text-xs font-bold text-gray-700 dark:text-gray-200">Loading messages...</p>
                    </div>
                ) : filteredMessages.length === 0 ? (
                    <div className="p-10 text-center text-gray-400 space-y-1">
                        <Mail className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600" />
                        <p className="text-xs font-bold text-gray-700 dark:text-gray-300">No contact messages found.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[700px]">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-750 text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase border-b border-gray-100 dark:border-gray-700">
                                    <th className="py-2 px-3">Date & Time</th>
                                    <th className="py-2 px-3">Contact Person & Phone</th>
                                    <th className="py-2 px-3">Subject & Message</th>
                                    <th className="py-2 px-3">Status</th>
                                    <th className="py-2 px-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-[11px]">
                                {filteredMessages.map((msg) => (
                                    <tr key={msg.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-750/50 transition-colors">
                                        <td className="py-2.5 px-3 font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                            <div className="flex items-center gap-1 text-gray-500">
                                                <Clock size={11} /> {formatDateTime(msg.created_at)}
                                            </div>
                                        </td>

                                        <td className="py-2.5 px-3">
                                            <div>
                                                <p className="font-bold text-gray-900 dark:text-white">
                                                    {msg.name}
                                                </p>
                                                <p className="text-[10px] text-gray-500">
                                                    {msg.email}
                                                </p>
                                                {msg.phone ? (
                                                    <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.2 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 rounded text-[10px] font-mono font-bold">
                                                        <Phone size={9} /> {msg.phone}
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] text-gray-400 italic">No phone provided</span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="py-2.5 px-3">
                                            <div className="max-w-md">
                                                <p className="font-extrabold text-emerald-700 dark:text-emerald-400 truncate">
                                                    {msg.subject}
                                                </p>
                                                <p className="text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2 leading-relaxed">
                                                    {msg.message}
                                                </p>
                                            </div>
                                        </td>

                                        <td className="py-2.5 px-3">
                                            <select
                                                value={msg.status || 'Pending'}
                                                disabled={updatingId === msg.id}
                                                onChange={(e) => handleStatusChange(msg.id, e.target.value)}
                                                className={`px-2 py-0.5 rounded text-[10px] font-extrabold border focus:outline-none cursor-pointer ${
                                                    msg.status === 'Resolved' 
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : msg.status === 'Replied'
                                                        ? 'bg-purple-50 text-purple-700 border-purple-200'
                                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                                }`}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Replied">Replied</option>
                                                <option value="Resolved">Resolved</option>
                                            </select>
                                        </td>

                                        <td className="py-2.5 px-3 text-right">
                                            <button
                                                onClick={() => handleDelete(msg.id)}
                                                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors"
                                                title="Delete Message"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

        </div>
    );
};

export default ContactMessages;
